import type { ParsedUrlQuery } from 'querystring';

/*
 * Builds the view-context message pushed over the websocket on every route
 * change so the assistant knows where each tab is. Backend handler:
 * alerts/websockets/connection_handler.py::handle_request_message.
 *
 * Only fields present in the message are set; omitted id fields are cleared
 * server-side (that's why we must send on EVERY navigation, not just company
 * pages — otherwise a stale company never clears). `current_page` is a string
 * that is only updated when present, so we always include it.
 */

export interface ViewContext {
  current_page: string;
  company?: string;
  portfolio?: string;
  registration_id?: string;
  batch_registration_id?: string;
  compliance_check_id?: string;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const asUuid = (v: string | string[] | undefined): string | undefined =>
  typeof v === 'string' && UUID_RE.test(v) ? v : undefined;

/** Derive a readable page name from the route (dynamic `[params]` stripped). */
export function pageName(pathname: string): string {
  const name = pathname
    .replace(/\/?\[[^\]]+\]/g, '')
    .replace(/^\/+/, '')
    .replace(/\/+/g, ' ')
    .trim();
  return name || 'dashboard';
}

export function buildViewContext(
  pathname: string,
  query: ParsedUrlQuery
): ViewContext {
  const ctx: ViewContext = { current_page: pageName(pathname) };
  // Route params ([company], [portfolio]) and query params surface on `query`.
  const company = asUuid(query.company);
  const portfolio = asUuid(query.portfolio);
  const registrationId = asUuid(query.registration_id);
  const batchRegistrationId = asUuid(query.batch_registration_id);
  const complianceCheckId = asUuid(query.compliance_check_id);
  if (company) ctx.company = company;
  if (portfolio) ctx.portfolio = portfolio;
  if (registrationId) ctx.registration_id = registrationId;
  if (batchRegistrationId) ctx.batch_registration_id = batchRegistrationId;
  if (complianceCheckId) ctx.compliance_check_id = complianceCheckId;
  return ctx;
}
